import { useLocation } from "@/lib/locations";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

const stepDelays = ["delay-100", "delay-200", "delay-300", "delay-400", "delay-500"];

export const HowItWorks = () => {
  const location = useLocation();
  const { ref: headingRef, isVisible: headingVisible } = useScrollAnimation();
  const { ref: stepsRef, isVisible: stepsVisible } = useScrollAnimation({ threshold: 0.08 });

  const steps = [
    {
      image: "https://vibe.filesafe.space/1776798830316075366/assets/c41a4ded-92f3-4616-8973-c05661f0695f.png",
      title: "1. Schedule us",
      description: "We're available every day of the week with cleans beginning as early as 8am."
    },
    {
      image: "https://vibe.filesafe.space/1776798830316075366/assets/388e368f-b4ee-4f22-bef1-8e427457f697.png",
      title: "2. Let us in",
      description: "Provide entry instructions and we'll be there!"
    },
    {
      image: "https://vibe.filesafe.space/1776798830316075366/assets/47e766aa-d9dd-41d0-b416-0fcf7e82d1ce.png",
      title: "3. We clean",
      description: "We rigorously execute on our checklists. Our cleaners are trained to uphold the Uplevel Cleaning quality."
    },
    {
      image: "https://vibe.filesafe.space/1776798830316075366/assets/9b087031-1df5-4482-97a5-bfc2dc310158.png",
      title: "4. Give feedback",
      description: "How'd we do? Answer our one question survey (or if you like, provide more detail)."
    },
    {
      image: "https://vibe.filesafe.space/1776798830316075366/assets/910ebe8a-b011-4c4f-a2ea-ed8bc8e7ca77.png",
      title: "5. Rinse & repeat",
      description: "Schedule recurring cleans to keep your place always healthy, clean, and fresh."
    }
  ];

  return (
    <section id="how-it-works" className="py-24 bg-background border-t">
      <div className="container">
        <div
          ref={headingRef}
          className={cn("text-center mb-16 anim-fade-up", headingVisible && "is-visible")}
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-4">How It Works?</h2>
          <div className="h-1.5 w-24 bg-primary mx-auto rounded-full" />
        </div>

        <div ref={stepsRef} className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-12 lg:gap-8">
          {steps.map((step, i) => (
            <div
              key={i}
              className={cn(
                "flex flex-row md:flex-col items-center text-left md:text-center group anim-fade-up bg-muted/20 md:bg-transparent p-4 md:p-0 rounded-2xl md:rounded-none border border-border md:border-none",
                stepDelays[i],
                stepsVisible && "is-visible"
              )}
            >
              <div className="relative w-20 h-20 shrink-0 mr-4 md:mr-0 md:mb-6 md:w-40 md:h-40 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                <img
                  srcSet={`${step.image}?w=160&q=80 160w, ${step.image}?w=400&q=80 400w`}
                  sizes="(max-width: 768px) 160px, 400px"
                  src={`${step.image}?w=400&q=80`}
                  alt={step.title}
                  loading="lazy"
                  decoding="async"
                  width="160"
                  height="160"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold mb-1 md:mb-3 text-foreground">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary/5 text-primary font-semibold text-sm border border-primary/10 anim-float">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            Reliable local service in {location.name}
          </div>
        </div>
      </div>
    </section>
  );
};
